<script setup lang="ts">
import { useGlobalSearch } from '@/widgets/GlobalSearch'
import { nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useConfirm, useNotification, VIcons } from '@/shared/ui'
import { useTranslate } from '@/shared/lib'
import type { GlobalSearchQueryShort } from '../model/type.ts'
import { cloneDeep } from 'lodash'
import { DateTime } from 'ts-luxon'
import WGlobalSearchCoords from '@/widgets/GlobalSearch/ui/WGlobalSearchCoords.vue'
import WGlobalSearchFilter from '@/widgets/GlobalSearch/ui/WGlobalSearchFilter.vue'
import { WObjectSubTypeSelect } from '@/widgets'
import WGlobalSearchResultTable from '@/widgets/GlobalSearch/ui/WGlobalSearchResultTable.vue'
import { stripFilterLocalKeys, useGlobalSearchNavigation } from '../model/helpers.ts'
import {
  findRangeValidationError,
  RANGE_VALIDATION_MESSAGES,
} from '../model/filterConditionUtils.ts'

const model = useGlobalSearch()
const route = useRoute()
const router = useRouter()
const { show } = useConfirm()
const { addError } = useNotification()
const { trs } = useTranslate()
const { navigateToMap } = useGlobalSearchNavigation()

const data = ref<GlobalSearchQueryShort>({
  q: '',
  object_type_id: 0,
  date: '',
  filter: [],
})

/**
 * Флаг выставляется на время searchHandler, чтобы обратный watch не перезаписал
 * data.value и не вызвал пересоздание компонентов условий фильтра.
 */
let isSyncing = false

/** Заполняем текущими данными из модели  */
watch(
  () => ({
    q: model.query.q,
    object_type_id: model.query.object_type_id,
    date: model.query.date,
    filter: model.query.filter,
  }),
  (query) => {
    if (isSyncing) return
    data.value = {
      q: query.q,
      object_type_id: Number(query.object_type_id),
      date: query.date,
      filter: cloneDeep(query.filter),
    }
  },
  { deep: true, immediate: true },
)

/**
 * Синхронизирует строку запроса в адресной строке с моделью поиска.
 * Пустые значения (null/''/строки 'null'|'undefined') вычищаются из query,
 * чтобы в URL не оставалось мусора вида area=null&sort_by=null.
 */
watch(
  () => ({
    q: model.query.q,
    sort_by: model.query.sort_by,
    sort_order: model.query.sort_order,
  }),
  (query) => {
    const nextQuery: Record<string, unknown> = {
      ...route.query,
      q: query.q,
      sort_by: query.sort_by,
      sort_order: query.sort_order,
    }

    for (const key of Object.keys(nextQuery)) {
      const value = nextQuery[key]
      if (value == null || value === '' || value === 'null' || value === 'undefined') {
        delete nextQuery[key]
      }
    }

    router.replace({ query: nextQuery as Record<string, string> }).catch(() => {})
  },
)

/**
 * При смене глобального типа объекта показывает подтверждение сброса фильтров,
 * если в черновике уже есть условия. При отмене откатывает значение.
 */
let isUpdatingType = false

watch(
  () => data.value.object_type_id,
  (val, oldVal) => {
    if (isUpdatingType) return
    if (!data.value.filter.flat().length) return
    isUpdatingType = true
    show({
      title: trs('itf.confirm_change_object_type_in_search_title'),
      content: trs('itf.confirm_change_object_type_in_search'),
      confirmFunc: () => {
        data.value.filter = []
        isUpdatingType = false
      },
      cancelFunc: () => {
        data.value.object_type_id = oldVal
        nextTick(() => {
          isUpdatingType = false
        })
      },
    })
  },
)

/**
 * Функция отправки данных на поиск
 */
const searchHandler = () => {
  const rangeError = findRangeValidationError(data.value.filter)
  if (rangeError) {
    addError(RANGE_VALIDATION_MESSAGES[rangeError])
    return
  }

  isSyncing = true
  model.query = {
    ...model.query,
    ...cloneDeep(data.value),
    q: data.value.q.trim(),
    filter: stripFilterLocalKeys(cloneDeep(data.value.filter)),
    page: 1,
  }
  nextTick(() => {
    isSyncing = false
  })
}

/**
 * Функция отображения найденных результатов поиска на карте
 */
const toMapHandler = () => {
  if (model.total > model.query.limit) {
    show({
      title: trs('itf.confirm_to_map.title'),
      content: trs('itf.confirm_to_map.desc', { limit: model.query.limit }),
      confirmFunc: () => {},
      confirmBtn: trs('itf.understand'),
      cancelBtn: '',
    })

    return
  }

  navigateToMap(model.result)
}

/**
 * Восстанавливает параметры поиска из query-string при открытии страницы расширенного поиска.
 */
onMounted(() => {
  // Пустая дата «по состоянию на» приводит к `400 q is required for non-advanced search`
  // при выборе только типа объекта без текстового запроса — подставляем сегодняшний день,
  // если он не задан ни в query-строке, ни уже в модели (например, из быстрого поиска).
  const resolvedDate = String(route.query.date ?? model.query.date ?? '')

  model.query = {
    ...model.query,
    q: String(route.query.q ?? model.query.q),
    object_type_id: Number(route.query.object_type_id ?? model.query.object_type_id),
    date: resolvedDate || (DateTime.now().toISODate() as string),
    area: route.query.area ? JSON.parse(String(route.query.area)) : null,
    limit: 50,
    page: 1,
    sort_by: route.query.sort_by ? String(route.query.sort_by) : model.query.sort_by,
    sort_order: route.query.sort_order
      ? (String(route.query.sort_order) as 'asc' | 'desc')
      : model.query.sort_order,
  }
})
</script>

<template>
  <div class="advanced-search-divider mt-2 mb-6">
    <VDivider>
      {{ $t('itf.search') }}
    </VDivider>
  </div>

  <div class="w-global-search__head_full">
    <WObjectSubTypeSelect
      hide-details
      :label="$t('itf.filter')"
      empty-value
      compact
      filtered
      v-model="data.object_type_id"
    />

    <VTextField v-model="data.q" ref="field" :placeholder="$t('itf.search')" hide-details>
      <template #prepend-inner>
        <VIcons type="search" />
      </template>
    </VTextField>

    <VTextField
      type="date"
      hide-details
      :label="$t('itf.position_from_date')"
      v-model="data.date"
    />

    <VBtn @click="searchHandler"> <VIcons type="search" />Найти</VBtn>

    <VTooltip location="top" :text="$t('itf.map')">
      <template #activator="{ props }">
        <VBtn
          :disabled="!model.result.length"
          v-bind="props"
          @click="toMapHandler"
        >
          <VIcons type="location_on" />
        </VBtn>
      </template>
    </VTooltip>
  </div>

  <WGlobalSearchCoords />

  <WGlobalSearchFilter v-model:filter="data.filter" :object-type-id="data.object_type_id" />

  <WGlobalSearchResultTable />
</template>
